import { useState, useEffect, useRef } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  lazy?: boolean;
  priority?: boolean;
  quality?: number;
  sizes?: string;
  placeholder?: 'blur' | 'empty';
  fetchpriority?: 'high' | 'low' | 'auto';
  onLoad?: () => void;
  onError?: () => void;
}

const OptimizedImage = ({ 
  src, 
  alt, 
  width, 
  height, 
  className = '', 
  lazy = true, 
  priority = false,
  quality = 80,
  sizes,
  placeholder = 'empty',
  fetchpriority,
  onLoad,
  onError
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(!lazy || priority);
  const [imageSrc, setImageSrc] = useState(priority ? src : '');
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || priority || isInView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '100px', // Start loading earlier for faster perceived performance
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [lazy, priority, isInView]);

  // Load image when in view
  useEffect(() => {
    if (isInView && !imageSrc) {
      setImageSrc(src);
    }
  }, [isInView, src, imageSrc]);

  // Use original source for reliability
  const getOptimizedSrc = (originalSrc: string) => {
    return originalSrc;
  };

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  // Simplified responsive images
  const generateSrcSet = (baseSrc: string) => {
    return ''; // Disabled for better performance and reliability
  };

  const imageClasses = `
    ${className} 
    transition-all duration-500 ease-out
    ${isLoaded ? 'opacity-100' : 'opacity-0'}
  `.trim();

  const placeholderStyles = {
    backgroundColor: placeholder === 'blur' ? '#f3f4f6' : 'transparent',
    backgroundImage: placeholder === 'blur' 
      ? 'linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)'
      : 'none',
    backgroundSize: placeholder === 'blur' ? '20px 20px' : 'auto',
    backgroundPosition: placeholder === 'blur' ? '0 0, 0 10px, 10px -10px, -10px 0px' : 'auto'
  };

  if (hasError) {
    return (
      <div 
        className={`${className} bg-muted flex items-center justify-center text-muted-foreground`}
        style={{ width, height }}
        role="img"
        aria-label={`Failed to load image: ${alt}`}
      >
        <span className="text-sm">Image not available</span>
      </div>
    );
  }

  return (
    <div ref={imgRef} className="relative" style={{ width, height }}>
      {/* Placeholder */}
      {!isLoaded && (
        <div 
          className={`absolute inset-0 ${imageClasses}`}
          style={placeholderStyles}
          aria-hidden="true"
        />
      )}
      
      {/* Actual Image */}
      {imageSrc && (
        <img
          src={getOptimizedSrc(imageSrc)}
          srcSet={generateSrcSet(getOptimizedSrc(imageSrc))}
          sizes={sizes || `${width}px`}
          alt={alt}
          width={width}
          height={height}
          className={imageClasses}
          loading={lazy && !priority ? 'lazy' : 'eager'}
          decoding="async"
          {...({ fetchpriority: fetchpriority || (priority ? 'high' : 'auto') } as any)}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            aspectRatio: width && height ? `${width}/${height}` : 'auto'
          }}
        />
      )}
    </div>
  );
};

// Hook for preloading critical images
export const useImagePreload = (imageSrcs: string[]) => {
  useEffect(() => {
    const preloadImages = imageSrcs.map(src => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
      return link;
    });

    return () => {
      preloadImages.forEach(link => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      });
    };
  }, [imageSrcs]);
};

// Component for critical above-the-fold images
export const CriticalImage = ({ priority = true, lazy = false, ...props }: OptimizedImageProps) => (
  <OptimizedImage {...props} lazy={lazy} priority={priority} />
);

export default OptimizedImage;